import {
  Button,
  ButtonGroup,
  Divider,
  FormGroup,
  HTMLSelect,
  InputGroup,
  Tag,
} from "@blueprintjs/core"
import { useQueryClient } from "@tanstack/react-query"
import { isEmpty } from "lodash"
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { FlexContainer } from "../../../../styled/flexbox.styled"
import { ConfirmDialog } from "../../../components"
import { createToaster } from "../../../utils/createToaster"
import Sectors from "../components/Sectors"
import useUpdateTradingInstrumentMutation from "../hooks/useUpdateTradingInstrumentMutation"
import { INSTRUMENT_ASSET_TYPES, InstrumentAssetType, InstrumentDetail } from "../types"
import { formatInstrumentForUpdate, prepareInstrumentForForm } from "./utils"
import { FieldsContainer, FormContainer, SectionTitle } from "./Instrument.styled"

interface IInstrumentFormProps {
  selectedInstrument: InstrumentDetail
  setSelectedInstrumentId: Dispatch<SetStateAction<number | undefined>>
}

type ConfirmDialogState = {
  open: boolean
  title: string
  dialogText: string
  handleClick: (() => void) | null
  confirmButtonText: string
}

const confirmDialogInitialState: ConfirmDialogState = {
  open: false,
  title: "",
  dialogText: "",
  handleClick: null,
  confirmButtonText: "",
}

const InstrumentForm = ({ selectedInstrument, setSelectedInstrumentId }: IInstrumentFormProps) => {
  const queryClient = useQueryClient()
  const [confirmDialog, setConfirmDialog] = useState(confirmDialogInitialState)

  const methods = useForm<InstrumentDetail>({
    defaultValues: prepareInstrumentForForm(selectedInstrument),
  })

  // Reset form when selectedInstrument changes
  useEffect(() => {
    if (selectedInstrument) {
      methods.reset(prepareInstrumentForForm(selectedInstrument))
    }
  }, [selectedInstrument, methods])

  // Mutation for updating instrument
  const updateInstrumentMutation = useUpdateTradingInstrumentMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trading_instruments"] })
      queryClient.invalidateQueries({ queryKey: ["trading_instrument", selectedInstrument.id] })
      createToaster({
        message: `${selectedInstrument.bloomberg_code} updated successfully`,
        intent: "success",
      })
    },
    onError: () => {
      methods.reset()
      createToaster({
        message: `An error occurred updating ${selectedInstrument.bloomberg_code}`,
        intent: "danger",
      })
    },
  })

  const submitForm = useCallback(
    (instrument: InstrumentDetail) => {
      const formattedInstrument = formatInstrumentForUpdate(instrument)
      updateInstrumentMutation.mutate({
        instrumentId: instrument.id,
        instrument: formattedInstrument,
      })
      setConfirmDialog(confirmDialogInitialState)
    },
    [updateInstrumentMutation],
  )

  const handleSubmitForm = () => {
    setConfirmDialog({
      open: true,
      title: "Update instrument",
      dialogText: "You are going to update this instrument's configuration.",
      handleClick: () => methods.handleSubmit(submitForm)(),
      confirmButtonText: "Update",
    })
  }

  const resetForm = useCallback(() => {
    methods.reset()
    setConfirmDialog(confirmDialogInitialState)
  }, [methods])

  const handleResetForm = () => {
    setConfirmDialog({
      open: true,
      title: "Reset changes",
      dialogText: "You are going to cancel your changes.",
      handleClick: resetForm,
      confirmButtonText: "Reset",
    })
  }

  return (
    <FormProvider {...methods}>
      <form>
        <FormContainer flexDirection="column" gap="20px">
          {/* Header with ticker and prices */}
          <FlexContainer alignItems="center" justifyContent="space-between">
            <FlexContainer alignItems="center" gap="15px">
              <SectionTitle>{selectedInstrument.bloomberg_code}</SectionTitle>

              {/* Price display */}
              <FlexContainer gap="8px">
                {selectedInstrument.eod_price != null && (
                  <Tag minimal intent="none">
                    EOD: {selectedInstrument.eod_price.toFixed(2)} {selectedInstrument.currency?.code}
                  </Tag>
                )}
                {selectedInstrument.latest_price_latency !== "eod" &&
                  selectedInstrument.latest_price != null && (
                    <Tag
                      minimal
                      intent={selectedInstrument.latest_price_latency === "live" ? "success" : "warning"}
                    >
                      {selectedInstrument.latest_price_latency === "live" ? "Live" : "Delayed"}:{" "}
                      {selectedInstrument.latest_price.toFixed(2)} {selectedInstrument.currency?.code}
                    </Tag>
                  )}
                {selectedInstrument.multiplication_factor != null &&
                  selectedInstrument.multiplication_factor !== 1 && (
                    <Tag minimal intent="primary">
                      Mult: {selectedInstrument.multiplication_factor}
                    </Tag>
                  )}
              </FlexContainer>
            </FlexContainer>

            {/* Status tags */}
            <FlexContainer gap="8px">
              <Tag intent={selectedInstrument.active ? "success" : "none"} minimal>
                {selectedInstrument.active ? "Active" : "Inactive"}
              </Tag>
              {selectedInstrument.core && (
                <Tag intent="primary" minimal>
                  Core
                </Tag>
              )}
              {selectedInstrument.is_main_company_listing && (
                <Tag intent="warning" minimal>
                  Main Ticker
                </Tag>
              )}
            </FlexContainer>
          </FlexContainer>

          <Divider />

          {/* Details Section (read-only) */}
          <FieldsContainer flexDirection="column" gap="15px">
            {/* Basic Info */}
            {selectedInstrument.security_name && (
              <FormGroup label="Name" inline>
                <InputGroup value={selectedInstrument.security_name} disabled fill />
              </FormGroup>
            )}

            {selectedInstrument.security_type && (
              <FormGroup label="Security Type" inline>
                <InputGroup value={selectedInstrument.security_type} disabled fill />
              </FormGroup>
            )}

            {selectedInstrument.security_type2 && (
              <FormGroup label="Security Type 2" inline>
                <InputGroup value={selectedInstrument.security_type2} disabled fill />
              </FormGroup>
            )}

            {selectedInstrument.issuer && (
              <FormGroup label="Issuer" inline>
                <InputGroup value={selectedInstrument.issuer} disabled fill />
              </FormGroup>
            )}

            {/* Market Info */}
            {selectedInstrument.exchange && (
              <FormGroup label="Exchange" inline>
                <InputGroup
                  value={`${selectedInstrument.exchange.name} (${selectedInstrument.exchange.mic})`}
                  disabled
                  fill
                />
              </FormGroup>
            )}

            {selectedInstrument.currency && (
              <FormGroup label="Currency" inline>
                <InputGroup value={selectedInstrument.currency.code} disabled fill />
              </FormGroup>
            )}

            {selectedInstrument.country && (
              <FormGroup label="Country" inline>
                <InputGroup value={selectedInstrument.country.name} disabled fill />
              </FormGroup>
            )}

            {selectedInstrument.country_issue && (
              <FormGroup label="Country of Issue" inline>
                <InputGroup value={selectedInstrument.country_issue.name} disabled fill />
              </FormGroup>
            )}

            {selectedInstrument.exchange_market_status && (
              <FormGroup label="Market Status" inline>
                <InputGroup value={selectedInstrument.exchange_market_status} disabled fill />
              </FormGroup>
            )}

            {selectedInstrument.trading_day_end_time && (
              <FormGroup label="Trading End Time" inline>
                <InputGroup value={selectedInstrument.trading_day_end_time} disabled fill />
              </FormGroup>
            )}

            {/* Relationships */}
            {selectedInstrument.company && (
              <FormGroup label="Company" inline>
                <InputGroup value={selectedInstrument.company.name} disabled fill />
              </FormGroup>
            )}

            {selectedInstrument.underlying && (
              <FormGroup label="Underlying" inline>
                <InputGroup value={selectedInstrument.underlying.bloomberg_code} disabled fill />
              </FormGroup>
            )}

            {selectedInstrument.maturity && (
              <FormGroup label="Maturity" inline>
                <InputGroup value={selectedInstrument.maturity} disabled fill />
              </FormGroup>
            )}

            {/* Identifiers */}
            {selectedInstrument.bb_unique && (
              <FormGroup label="BB Unique" inline>
                <InputGroup value={selectedInstrument.bb_unique} disabled fill />
              </FormGroup>
            )}

            {selectedInstrument.sedol && (
              <FormGroup label="SEDOL" inline>
                <InputGroup value={selectedInstrument.sedol} disabled fill />
              </FormGroup>
            )}

            {/* Ratings */}
            {selectedInstrument.rtg_sp && (
              <FormGroup label="S&P Rating" inline>
                <InputGroup value={selectedInstrument.rtg_sp} disabled fill />
              </FormGroup>
            )}

            {selectedInstrument.rtg_moody && (
              <FormGroup label="Moody's Rating" inline>
                <InputGroup value={selectedInstrument.rtg_moody} disabled fill />
              </FormGroup>
            )}

            {selectedInstrument.rtg_fitch && (
              <FormGroup label="Fitch Rating" inline>
                <InputGroup value={selectedInstrument.rtg_fitch} disabled fill />
              </FormGroup>
            )}

            {/* Fund Info */}
            {selectedInstrument.fund_total_assets !== null &&
              selectedInstrument.fund_total_assets !== undefined && (
                <FormGroup label="Fund Total Assets" inline>
                  <InputGroup
                    value={selectedInstrument.fund_total_assets.toLocaleString()}
                    disabled
                    fill
                  />
                </FormGroup>
              )}

            {selectedInstrument.fund_euro_direct_ucits !== null &&
              selectedInstrument.fund_euro_direct_ucits !== undefined && (
                <FormGroup label="UCITS Fund" inline>
                  <InputGroup
                    value={selectedInstrument.fund_euro_direct_ucits ? "Yes" : "No"}
                    disabled
                    fill
                  />
                </FormGroup>
              )}

            {selectedInstrument.fund_leverage !== null &&
              selectedInstrument.fund_leverage !== undefined && (
                <FormGroup label="Fund Leverage" inline>
                  <InputGroup
                    value={selectedInstrument.fund_leverage ? "Yes" : "No"}
                    disabled
                    fill
                  />
                </FormGroup>
              )}
          </FieldsContainer>

          {/* Configuration Section (editable) */}
          <Divider />
          <FieldsContainer flexDirection="column" gap="15px">
            <SectionTitle>Configuration</SectionTitle>

            <FormGroup label="Asset Type" inline>
              <HTMLSelect
                value={methods.watch("asset_type")}
                onChange={(e) =>
                  methods.setValue("asset_type", e.target.value as InstrumentAssetType, {
                    shouldDirty: true,
                  })
                }
                fill
              >
                {INSTRUMENT_ASSET_TYPES.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </HTMLSelect>
            </FormGroup>

            {/* Sectors - only show if can_edit_sectors */}
            {selectedInstrument.can_edit_sectors && <Sectors />}
          </FieldsContainer>

          {/* Action buttons - only show when dirty */}
          {methods.formState.isDirty && (
            <FlexContainer justifyContent="flex-end">
              <ButtonGroup large>
                <Button text="Reset" icon="reset" onClick={handleResetForm} />
                <Button
                  text="Update"
                  icon="upload"
                  intent="primary"
                  onClick={handleSubmitForm}
                  disabled={!isEmpty(methods.formState.errors)}
                />
              </ButtonGroup>
            </FlexContainer>
          )}
        </FormContainer>

        <ConfirmDialog
          title={confirmDialog.title}
          dialogText={confirmDialog.dialogText}
          open={confirmDialog.open}
          close={() => setConfirmDialog(confirmDialogInitialState)}
          handleClick={confirmDialog.handleClick as () => void}
          confirmButtonText={confirmDialog.confirmButtonText}
        />
      </form>
    </FormProvider>
  )
}

export default InstrumentForm
